import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/baoyu';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams();
  const article = articles.find((a) => a.slug === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-500 font-medium whitespace-nowrap hover:text-primary-600">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: 'baoyu',
    },
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '文章', path: '/category/all' },
    { label: article.title },
  ];

  const categoryLabel =
    article.category === 'news'
      ? '新闻'
      : article.category === 'review'
        ? '评测'
        : article.category === 'guide'
          ? '指南'
          : '故事';

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <main className="min-h-screen bg-background-50">
        <article className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-20">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-2">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-medium">
              {categoryLabel}
            </span>
          </div>

          <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-sm text-foreground-600 mb-8">
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.date}</span>
            <span>·</span>
            <span>阅读时间 {article.readTime}</span>
          </div>

          <div className="relative w-full aspect-[2/1] rounded-2xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose-custom">
            {article.content.split('\n\n').map((paragraph, i) => (
              <p
                key={i}
                className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5"
              >
                {paragraph.trim()}
              </p>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs border border-background-200/70">
                baoyu
              </span>
              <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs border border-background-200/70">
                {categoryLabel}
              </span>
            </div>
          </div>
        </article>

        {/* Related articles */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 pb-20">
          <h2 className="text-xl font-bold text-foreground-950 mb-6">相关文章</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedArticles.map((ra) => (
              <Link
                key={ra.id}
                to={`/article/${ra.slug}`}
                className="group bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-background-300/60 cursor-pointer"
              >
                <div className="relative w-full aspect-[16/9] overflow-hidden">
                  <img
                    src={ra.coverImage}
                    alt={ra.title}
                    title={ra.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                    {ra.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-2">{ra.date}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </>
  );
}